import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator';
import { toastSuccess, toastError } from '@/components/ui/use-toast';
import { useTranslation } from '@/hooks/useTranslation';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const { t } = useTranslation();

  // Password validation function (same as SignUp page)
  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push(t('resetPassword.validation.minLength'));
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t('resetPassword.validation.uppercase'));
    }
    if (!/[a-z]/.test(password)) {
      errors.push(t('resetPassword.validation.lowercase'));
    }
    if (!/\d/.test(password)) {
      errors.push(t('resetPassword.validation.number'));
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t('resetPassword.validation.specialChar'));
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
      return;
    }

    validateToken();
  }, [token, navigate]);

  const validateToken = async () => {
    try {
      const response = await fetch('https://52.196.239.9:5000/api/auth/validate-reset-token', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setTokenValid(true);
        setUserEmail(data.email);
        setExpiresAt(data.expires_at);
      } else {
        setTokenValid(false);
        toastError({
          title: t('resetPassword.errors.invalidToken'),
          description: data.message || t('resetPassword.errors.invalidTokenDescription'),
        });
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setTokenValid(false);
      toastError({
        title: t('resetPassword.errors.validationFailed'),
        description: t('resetPassword.errors.validationFailedDescription'),
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toastError({
        title: t('common.error'),
        description: t('resetPassword.errors.fillAllFields'),
      });
      return;
    }

    if (password !== confirmPassword) {
      toastError({
        title: t('common.error'),
        description: t('resetPassword.errors.passwordsDoNotMatch'),
      });
      return;
    }

    // Validate password requirements (same as SignUp page)
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toastError({
        title: t('resetPassword.errors.requirementsNotMet'),
        description: passwordValidation.errors.join('. '),
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://52.196.239.9:5000/api/auth/reset-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          token,
          password 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          // Show password validation errors from backend
          toastError({
            title: t('resetPassword.errors.requirementsNotMet'),
            description: data.errors.join('. '),
          });
        } else {
          throw new Error(data.message || t('resetPassword.errors.resetFailed'));
        }
        return;
      }

      setIsSuccess(true);
      toastSuccess({
        title: t('resetPassword.success.title'),
        description: data.message,
      });

    } catch (error) {
      console.error('Password reset failed:', error);
      toastError({
        title: t('resetPassword.errors.resetFailedTitle'),
        description: error instanceof Error ? error.message : t('resetPassword.errors.resetFailed'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('resetPassword.validating.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('resetPassword.validating.description')}
          </p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('resetPassword.invalid.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('resetPassword.invalid.description')}
          </p>
        </div>

        <div className="grid gap-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
            <h3 className="font-medium text-red-900 dark:text-red-100 mb-2">{t('resetPassword.invalid.linkInvalid')}</h3>
            <div className="text-sm text-red-800 dark:text-red-200">
              <p className="mb-1">{t('resetPassword.invalid.reason1')}</p>
              <p className="mb-1">{t('resetPassword.invalid.reason2')}</p>
              <p>{t('resetPassword.invalid.reason3')}</p>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <Button asChild className="w-full">
              <Link to="/forgot-password">{t('resetPassword.invalid.requestNewLink')}</Link>
            </Button>

            <Button asChild variant="ghost" className="w-full">
              <Link to="/login">{t('resetPassword.invalid.backToLogin')}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('resetPassword.success.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('resetPassword.success.description')}
          </p>
        </div>

        <div className="grid gap-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
            <h3 className="font-medium text-green-900 dark:text-green-100 mb-2">{t('resetPassword.success.passwordUpdated')}</h3>
            <p className="text-sm text-green-800 dark:text-green-200">
              {t('resetPassword.success.securityNote')}
            </p>
          </div>

          <Button asChild className="w-full">
            <Link to="/login">{t('resetPassword.success.continueToLogin')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t('resetPassword.form.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('resetPassword.form.description', { email: userEmail })}
        </p>
      </div>

      <div className="grid gap-6">
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="password">{t('resetPassword.form.newPassword')}</Label>
              <Input
                id="password"
                placeholder={t('resetPassword.form.newPasswordPlaceholder')}
                type="password"
                autoComplete="new-password"
                disabled={isLoading}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {password && (
                <PasswordStrengthIndicator password={password} className="mt-2" />
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">{t('resetPassword.form.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                placeholder={t('resetPassword.form.confirmPasswordPlaceholder')}
                type="password"
                autoComplete="new-password"
                disabled={isLoading}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <Button disabled={isLoading} className="w-full">
              {isLoading ? t('resetPassword.form.updating') : t('resetPassword.form.updateButton')}
            </Button>
          </div>
        </form>

        <div className="flex flex-col space-y-2 text-center">
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">{t('resetPassword.form.backToLogin')}</Link>
          </Button>
        </div>
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
        <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">{t('resetPassword.requirements.title')}</h3>
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <p className="mb-1">{t('resetPassword.requirements.minLength')}</p>
          <p className="mb-1">{t('resetPassword.requirements.caseLetters')}</p>
          <p className="mb-1">{t('resetPassword.requirements.number')}</p>
          <p>{t('resetPassword.requirements.specialChar')}</p>
        </div>
      </div>

      {expiresAt && (
        <div className="text-center text-sm text-muted-foreground">
          {t('resetPassword.expiration.notice', { time: new Date(expiresAt).toLocaleString() })}
        </div>
      )}
    </div>
  );
}
